import { OrderStage } from "../helpers";

import { Icon, H2 } from "@sellar-io/components";
import {
  TooltipDismiss,
  TooltipDismissContent,
} from "../../tooltipDismiss/TooltipDismiss";
import { ReactNode, useState } from "react";
import dayjs from "dayjs";
import { useSellarCreditPaymentStatus } from "../useSellarCreditPaymentStatus";

const preDispatchOrderStages: OrderStage[] = [
  "Accepted",
  "Requested",
  "Confirmed",
];

const preConfirmedOrderStages: OrderStage[] = ["Accepted", "Requested"];

type SellarCreditOrderPaymentStatusProps = {
  orderId: number;
  orderStage: OrderStage;
  invoicePaidAt: Date | null;
  userType: "buyer" | "supplier";
  initiator: "retailer" | "supplier" | null;
};

export const SellarCreditOrderPaymentStatus = ({
  userType,
  ...props
}: SellarCreditOrderPaymentStatusProps) => {
  if (userType === "buyer") {
    return <SellarCreditOrderPaymentStatusBuyer {...props} />;
  }
  if (userType === "supplier") {
    return <SellarCreditOrderPaymentStatusSupplier {...props} />;
  }
  return null;
};

const IconWithTooltip = ({
  description,
  title,
}: {
  description: ReactNode;
  title: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex flex-col">
      {open && (
        <TooltipDismiss>
          <TooltipDismissContent
            icon="INFORMATION_OUTLINE"
            iconSize={0.67}
            title={title}
            description={description}
            onClick={() => setOpen((prevOpen) => !prevOpen)}
            showAnchor={false}
          ></TooltipDismissContent>
        </TooltipDismiss>
      )}
      <Icon
        onClick={() => setOpen(true)}
        icon="INFORMATION_OUTLINE"
        className="text-zinc-500"
      />
    </div>
  );
};

const SellarCreditOrderPaymentStatusBuyer = ({
  invoicePaidAt,
  orderStage,
  initiator,
  orderId,
}: Omit<SellarCreditOrderPaymentStatusProps, "userType">) => {
  const { isProcessed: orderHasBeenProcessed } = useSellarCreditPaymentStatus({
    orderId,
  });
  if (invoicePaidAt) {
    return (
      <div className="flex items-center gap-2">
        <H2 className="text-gray-500">
          Settled on {dayjs(invoicePaidAt).format("DD/MM/YYYY")}
        </H2>
        <IconWithTooltip
          title="Added to your tab"
          description={
            <div className="flex flex-col justify-center gap-2">
              <span>This payment is made to Two not your supplier</span>
              <a
                href="https://intercom.help/sellario/en/articles/10002894-supplier-guide-order-statuses"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-0 py-0.5 text-xs font-semibold text-black uppercase hover:bg-transparent"
              >
                <span>Learn more</span>
                <Icon
                  icon="CHEVRON_RIGHT"
                  className="relative bottom-[1px]"
                  size={0.7}
                />
              </a>
            </div>
          }
        />
      </div>
    );
  }

  if (preConfirmedOrderStages.includes(orderStage) && !orderHasBeenProcessed) {
    return (
      <H2 className="text-gray-500">
        {initiator === "supplier" ? "Awaiting confirmation" : "Retry payment"}
      </H2>
    );
  }

  if (preDispatchOrderStages.includes(orderStage)) {
    return <H2 className="text-gray-500">Added to your tab</H2>;
  }

  if (orderStage === "Dispatched") {
    return (
      <div className="flex items-center gap-2">
        <H2 className="flex text-gray-500">Added to your tab</H2>
        <IconWithTooltip
          title="Added to your tab"
          description={
            <div className="flex flex-col justify-center gap-2">
              <span>This payment is made to Two not your supplier</span>
              <a
                href="https://intercom.help/sellario/en/articles/10002894-supplier-guide-order-statuses"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-0 py-0.5 text-xs font-semibold text-black uppercase hover:bg-transparent"
              >
                <span>Learn more</span>
                <Icon
                  icon="CHEVRON_RIGHT"
                  className="relative bottom-[1px]"
                  size={0.7}
                />
              </a>
            </div>
          }
        />
      </div>
    );
  }

  if (orderStage === "Delivered") {
    return <H2 className="text-gray-500">Added to your tab</H2>;
  }

  if (orderStage === "Cancelled") {
    return <H2 className="text-gray-500">Voided</H2>;
  }

  return null;
};

const SellarCreditOrderPaymentStatusSupplier = ({
  orderStage,
  invoicePaidAt,
}: Omit<SellarCreditOrderPaymentStatusProps, "userType">) => {
  if (invoicePaidAt) {
    return (
      <H2 className="text-gray-500">
        Paid on {dayjs(invoicePaidAt).format("DD/MM/YYYY")}
      </H2>
    );
  }

  if (orderStage === "Accepted") {
    return <H2 className="text-gray-500">Awaiting finalisation</H2>;
  }

  if (orderStage === "Confirmed") {
    return (
      <div className="flex items-center gap-2">
        <H2 className="flex text-gray-500">Prepare for dispatch</H2>
        <IconWithTooltip
          title="Prepare for dispatch"
          description={
            <div className="flex flex-col justify-center gap-2">
              <span>
                No need to invoice this order. This is handled by Sellar Credit
              </span>
              <a
                href="https://intercom.help/sellario/en/articles/10709865-supplier-guide-how-do-payouts-work-for-sellar-credit"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-0 py-0.5 text-xs font-semibold text-black uppercase hover:bg-transparent"
              >
                <span>Learn more</span>
                <Icon
                  icon="CHEVRON_RIGHT"
                  className="relative bottom-[1px]"
                  size={0.7}
                />
              </a>
            </div>
          }
        />
      </div>
    );
  }

  if (orderStage === "Dispatched") {
    return <H2 className="text-gray-500">Funds processing</H2>;
  }

  if (orderStage === "Delivered") {
    return <H2 className="text-gray-500">Funds processing</H2>;
  }

  if (orderStage === "Cancelled") {
    return <H2 className="text-gray-500">Voided</H2>;
  }
  return null;
};
